/**
 * RECURRENCE (F1) — pure IN-WINDOW occurrence materialization.
 *
 * RRULE (RFC 5545) subset sufficient for church meetings:
 *   FREQ=DAILY|WEEKLY|MONTHLY|YEARLY · INTERVAL · COUNT · UNTIL ·
 *   BYDAY (weekly: list of days; monthly: ordinal `1SU`/`-1FR`) ·
 *   BYMONTHDAY (monthly).
 *
 * The series preserves WALL CLOCK time in the church's time zone
 * (`meeting.timezone`, ALWAYS explicit): "Sundays at 10:00" remains
 * 10:00 local across daylight-saving (DST) changes,
 * even though the UTC instant changes. Per-occurrence exceptions
 * (`meeting_exception`) cancel or move ONE instance.
 *
 * No IO, no dependencies: usable on the client (local-first calendar),
 * tests and any runtime with `Intl`. Design in docs/RECURRENCE.md.
 */

export type RRuleFreq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
export type RRuleWeekday = (typeof WEEKDAYS)[number];

export interface RRuleSpec {
  freq: RRuleFreq;
  interval: number;
  count?: number;
  /** Inclusive UTC instant (ms). */
  until?: number;
  /** WEEKLY: days of the week. */
  byDay?: RRuleWeekday[];
  /** MONTHLY: day of month (1..31). */
  byMonthDay?: number;
  /** MONTHLY: ordinal day, e.g. `{ ordinal: 1, weekday: "SU" }` (= 1st Sunday); ordinal -1 = last. */
  byDayOrdinal?: { ordinal: number; weekday: RRuleWeekday };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Hard iteration cap when enumerating a series (processes windows, not infinite series). */
const MAX_ITERATIONS = 2000;
const MS_PER_DAY = 86_400_000;

// ─── Parsing ─────────────────────────────────────────────────────────────────

function parseUntil(value: string): number | undefined {
  // YYYYMMDD or YYYYMMDDTHHMMSSZ (RFC 5545). Bare date = end of that UTC day.
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(value.trim());
  if (!m) {
    return undefined;
  }
  const [, y, mo, d, hh, mi, ss] = m;
  if (hh === undefined) {
    return Date.UTC(Number(y), Number(mo) - 1, Number(d), 23, 59, 59, 999);
  }
  return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mi), Number(ss));
}

function isWeekday(token: string): token is RRuleWeekday {
  return (WEEKDAYS as readonly string[]).includes(token);
}

/**
 * Parses the RRULE subset. Returns `null` if the rule is empty or uses
 * parts outside the subset (the caller decides whether to treat it as non-recurring).
 */
export function parseRRule(rrule: string | null | undefined): RRuleSpec | null {
  if (!rrule || !rrule.trim()) {
    return null;
  }
  const parts = rrule
    .trim()
    .replace(/^RRULE:/i, "")
    .split(";")
    .filter(Boolean);
  const map = new Map<string, string>();
  for (const part of parts) {
    const eq = part.indexOf("=");
    if (eq <= 0) {
      return null;
    }
    map.set(part.slice(0, eq).toUpperCase(), part.slice(eq + 1));
  }
  const freq = map.get("FREQ")?.toUpperCase();
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY" && freq !== "YEARLY") {
    return null;
  }
  const spec: RRuleSpec = { freq, interval: 1 };
  const interval = map.get("INTERVAL");
  if (interval !== undefined) {
    const n = Number(interval);
    if (!Number.isInteger(n) || n < 1) {
      return null;
    }
    spec.interval = n;
  }
  const count = map.get("COUNT");
  if (count !== undefined) {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1) {
      return null;
    }
    spec.count = n;
  }
  const until = map.get("UNTIL");
  if (until !== undefined) {
    const t = parseUntil(until);
    if (t === undefined) {
      return null;
    }
    spec.until = t;
  }
  const byDay = map.get("BYDAY");
  if (byDay !== undefined) {
    const tokens = byDay.split(",").map((t) => t.trim().toUpperCase());
    if (freq === "MONTHLY") {
      // A single ordinal day: `1SU`, `2MO`, `-1FR`…
      if (tokens.length !== 1) {
        return null;
      }
      const m = /^(-?\d)([A-Z]{2})$/.exec(tokens[0] ?? "");
      const weekday = m?.[2];
      if (!m || weekday === undefined || !isWeekday(weekday)) {
        return null;
      }
      const ordinal = Number(m[1]);
      if (ordinal === 0 || ordinal > 5 || ordinal < -1) {
        return null;
      }
      spec.byDayOrdinal = { ordinal, weekday };
    } else {
      if (!tokens.every(isWeekday)) {
        return null;
      }
      spec.byDay = tokens;
    }
  }
  const byMonthDay = map.get("BYMONTHDAY");
  if (byMonthDay !== undefined) {
    const n = Number(byMonthDay);
    if (!Number.isInteger(n) || n < 1 || n > 31) {
      return null;
    }
    spec.byMonthDay = n;
  }
  return spec;
}

// ─── Time zone: UTC instant ⇄ wall clock ────────────────────────────────────

interface WallClock {
  year: number;
  month: number; // 1..12
  day: number; // 1..31
  hour: number;
  minute: number;
  second: number;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let dtf = dtfCache.get(timeZone);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    dtfCache.set(timeZone, dtf);
  }
  return dtf;
}

/** Wall clock of a UTC instant in an IANA zone. */
export function utcToWallClock(utcMs: number, timeZone: string): WallClock {
  const parts = formatter(timeZone).formatToParts(new Date(utcMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") % 24, // h23 may emit 24 in some runtimes
    minute: get("minute"),
    second: get("second"),
  };
}

function wallAsUtcMs(w: WallClock): number {
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
}

/**
 * UTC instant of a wall clock in an IANA zone (double correction to cross
 * offset/DST changes; nonexistent hours fall back to the previous offset).
 */
export function wallClockToUtc(wall: WallClock, timeZone: string): number {
  const target = wallAsUtcMs(wall);
  const offsetAt = (utcMs: number) => wallAsUtcMs(utcToWallClock(utcMs, timeZone)) - utcMs;
  const first = target - offsetAt(target);
  const second = target - offsetAt(first);
  return second;
}

/** Day of week (0=Sunday) of a wall DATE (zone-independent). */
function wallWeekday(w: WallClock): number {
  return new Date(Date.UTC(w.year, w.month - 1, w.day)).getUTCDay();
}

/** Adds days to a wall date, preserving the time. */
function addDays(w: WallClock, days: number): WallClock {
  const d = new Date(Date.UTC(w.year, w.month - 1, w.day + days));
  return {
    ...w,
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  };
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Day of month of the ordinal `1SU`/`-1FR`…; `null` if it does not exist (e.g. 5th Monday). */
function ordinalWeekdayDay(
  year: number,
  month: number,
  ordinal: number,
  weekday: RRuleWeekday,
): number | null {
  const target = WEEKDAYS.indexOf(weekday);
  if (ordinal > 0) {
    const firstDow = wallWeekday({ year, month, day: 1, hour: 0, minute: 0, second: 0 });
    const day = 1 + ((target - firstDow + 7) % 7) + (ordinal - 1) * 7;
    return day <= daysInMonth(year, month) ? day : null;
  }
  // -1 = last <weekday> of the month.
  const last = daysInMonth(year, month);
  const lastDow = wallWeekday({ year, month, day: last, hour: 0, minute: 0, second: 0 });
  return last - ((lastDow - target + 7) % 7);
}

// ─── Series enumeration ──────────────────────────────────────────────────────

/**
 * Enumerates the series' UTC instants (ms) from DTSTART, in order, until
 * COUNT/UNTIL is exhausted or `windowTo` is passed. DTSTART ALWAYS counts as the first
 * occurrence (RFC 5545 convention).
 */
function enumerateSeries(
  dtstartMs: number,
  timeZone: string,
  spec: RRuleSpec,
  windowToMs: number,
): number[] {
  const out: number[] = [];
  const start = utcToWallClock(dtstartMs, timeZone);
  const limit = spec.until === undefined ? windowToMs : Math.min(spec.until, windowToMs);
  const wanted = spec.count ?? Number.POSITIVE_INFINITY;

  const push = (wall: WallClock): boolean => {
    const utc = wallClockToUtc(wall, timeZone);
    if (utc < dtstartMs) {
      return true; // before series start (e.g. prior BYDAY in week 0)
    }
    if (spec.until !== undefined && utc > spec.until) {
      return false;
    }
    if (out.length >= wanted) {
      return false;
    }
    out.push(utc);
    // With COUNT we must keep counting even after the window is behind… but
    // since we only deliver up to windowTo, we can stop once past it.
    return utc <= limit && out.length < wanted;
  };

  switch (spec.freq) {
    case "DAILY": {
      for (let i = 0; i < MAX_ITERATIONS; i += 1) {
        if (!push(addDays(start, i * spec.interval))) {
          break;
        }
      }
      break;
    }
    case "WEEKLY": {
      const days = spec.byDay?.length
        ? spec.byDay.map((d) => WEEKDAYS.indexOf(d)).toSorted((a, b) => a - b)
        : [wallWeekday(start)];
      // Anchor: the Sunday (start of week) of DTSTART's week.
      const weekAnchor = addDays(start, -wallWeekday(start));
      outer: for (let week = 0; week < MAX_ITERATIONS; week += 1) {
        for (const dow of days) {
          const wall = addDays(weekAnchor, week * spec.interval * 7 + dow);
          if (!push(wall)) {
            break outer;
          }
        }
      }
      break;
    }
    case "MONTHLY": {
      for (let i = 0; i < MAX_ITERATIONS; i += 1) {
        const totalMonths = start.month - 1 + i * spec.interval;
        const year = start.year + Math.floor(totalMonths / 12);
        const month = (totalMonths % 12) + 1;
        let day: number | null;
        if (spec.byDayOrdinal) {
          day = ordinalWeekdayDay(
            year,
            month,
            spec.byDayOrdinal.ordinal,
            spec.byDayOrdinal.weekday,
          );
        } else {
          const wantedDay = spec.byMonthDay ?? start.day;
          day = wantedDay <= daysInMonth(year, month) ? wantedDay : null;
        }
        if (day === null) {
          continue; // month without that date (Feb 31, 5th Monday…)
        }
        if (!push({ ...start, year, month, day })) {
          break;
        }
      }
      break;
    }
    case "YEARLY": {
      for (let i = 0; i < MAX_ITERATIONS; i += 1) {
        const year = start.year + i * spec.interval;
        if (start.day > daysInMonth(year, start.month)) {
          continue; // Feb 29 on non-leap year
        }
        if (!push({ ...start, year })) {
          break;
        }
      }
      break;
    }
  }
  return out;
}

// ─── Materialization with exceptions ────────────────────────────────────────

export interface MeetingLike {
  id: string;
  starts_at: string | null;
  ends_at?: string | null;
  timezone?: string | null;
  rrule?: string | null;
}

export interface ExceptionLike {
  id: string;
  meeting_id: string | null;
  occurrence_start: string | null;
  kind: string | null; // 'cancelled' | 'moved'
  new_start?: string | null;
  new_end?: string | null;
}

export type OccurrenceStatus = "scheduled" | "moved" | "cancelled";

export interface Occurrence {
  meetingId: string;
  /** UTC instant (ms) of the series' ORIGINAL occurrence (exception/attendance key). */
  originalStart: number;
  /** Effective instant after applying exceptions. */
  start: number;
  end: number | null;
  status: OccurrenceStatus;
  exceptionId?: string;
}

/** Parses local SQLite timestamps ("YYYY-MM-DD HH:MM:SS…") and RFC 3339. */
export function parseInstant(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const iso = value.includes("T") ? value : value.replace(" ", "T");
  // Postgres emits "+00" (two digits); Date.parse requires "+00:00".
  const normalized = /[+-]\d{2}$/.test(iso) ? `${iso}:00` : iso;
  const withZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
  const t = Date.parse(withZone);
  return Number.isNaN(t) ? null : t;
}

export interface OccurrenceWindow {
  /** Inclusive, ms UTC. */
  from: number;
  /** Inclusive, ms UTC. */
  to: number;
}

export const DEFAULT_TIMEZONE = "Europe/Madrid";

/**
 * Occurrences of ONE meeting within the window, with exceptions applied.
 * Meeting without `rrule` (or with an rrule outside the subset) = single occurrence.
 * Cancelled ones are RETURNED with `status: "cancelled"` (the UI decides whether to
 * strike them through or hide them); moved ones use their new instant (and enter/leave
 * the window according to that instant).
 */
export function materializeOccurrences(
  meeting: MeetingLike,
  window: OccurrenceWindow,
  exceptions: readonly ExceptionLike[] = [],
): Occurrence[] {
  const dtstart = parseInstant(meeting.starts_at);
  if (dtstart === null) {
    return [];
  }
  const timeZone = meeting.timezone?.trim() || DEFAULT_TIMEZONE;
  const endMs = parseInstant(meeting.ends_at);
  const durationMs = endMs !== null && endMs > dtstart ? endMs - dtstart : null;
  const spec = parseRRule(meeting.rrule);
  const starts = spec ? enumerateSeries(dtstart, timeZone, spec, window.to) : [dtstart];

  const byOriginal = new Map<number, ExceptionLike>();
  for (const ex of exceptions) {
    if (ex.meeting_id !== meeting.id) {
      continue;
    }
    const t = parseInstant(ex.occurrence_start);
    if (t !== null) {
      byOriginal.set(t, ex);
    }
  }

  const out: Occurrence[] = [];
  for (const originalStart of starts) {
    const ex = byOriginal.get(originalStart);
    let start = originalStart;
    let end = durationMs === null ? null : originalStart + durationMs;
    let status: OccurrenceStatus = "scheduled";
    let exceptionId: string | undefined;
    if (ex) {
      exceptionId = ex.id;
      if (ex.kind === "cancelled") {
        status = "cancelled";
      } else if (ex.kind === "moved") {
        status = "moved";
        const movedStart = parseInstant(ex.new_start);
        if (movedStart !== null) {
          start = movedStart;
          const movedEnd = parseInstant(ex.new_end);
          end =
            movedEnd === null ? (durationMs === null ? null : movedStart + durationMs) : movedEnd;
        }
      }
    }
    if (start < window.from || start > window.to) {
      continue;
    }
    out.push({ meetingId: meeting.id, originalStart, start, end, status, exceptionId });
  }
  return out.toSorted((a, b) => a.start - b.start);
}

/** Default calendar/detail window: [today - 7d, today + N days]. */
export function defaultWindow(nowMs: number, daysAhead = 60, daysBack = 7): OccurrenceWindow {
  return { from: nowMs - daysBack * MS_PER_DAY, to: nowMs + daysAhead * MS_PER_DAY };
}

// ─── RRULE builder (for the UI) ─────────────────────────────────────────────

/** Serializes the subset to RRULE text (inverse of `parseRRule`). */
export function buildRRule(spec: RRuleSpec): string {
  const parts = [`FREQ=${spec.freq}`];
  if (spec.interval > 1) {
    parts.push(`INTERVAL=${spec.interval}`);
  }
  if (spec.byDay?.length) {
    parts.push(`BYDAY=${spec.byDay.join(",")}`);
  }
  if (spec.byDayOrdinal) {
    parts.push(`BYDAY=${spec.byDayOrdinal.ordinal}${spec.byDayOrdinal.weekday}`);
  }
  if (spec.byMonthDay !== undefined) {
    parts.push(`BYMONTHDAY=${spec.byMonthDay}`);
  }
  if (spec.count !== undefined) {
    parts.push(`COUNT=${spec.count}`);
  }
  if (spec.until !== undefined) {
    const d = new Date(spec.until);
    parts.push(
      `UNTIL=${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`,
    );
  }
  return parts.join(";");
}

export { WEEKDAYS as RRULE_WEEKDAYS };
